import { useState, useEffect } from "react";
import api from "../../../../utils/api";
import { flattenProducts, filterProducts } from "../utils/availabilityUtils";
import { DEFAULT_PAGE_SIZE, MESSAGES } from "../utils/constants";

/**
 * Custom hook for product availability logic
 * Manages state and side effects for product availability display
 *
 * @returns {Object} Hook state and handlers
 * @returns {Array} returns.products - Raw products from API
 * @returns {Array} returns.filteredProducts - Filtered products
 * @returns {Array} returns.flattenedData - Flattened products for display
 * @returns {string} returns.searchQuery - Search query
 * @returns {number} returns.page - Current page
 * @returns {number} returns.rowsPerPage - Rows per page
 * @returns {boolean} returns.loading - Loading state
 * @returns {Function} returns.handleSearch - Handle search input
 * @returns {Function} returns.setPage - Set page number
 */
export const useProductAvailability = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(true);

  // Fetch products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await api.get("/products/availability?limit=0");
        const fetchedProducts = response.data.products || [];
        setProducts(fetchedProducts);
        setFilteredProducts(fetchedProducts);
      } catch (err) {
        console.error(MESSAGES.ERROR_FETCHING, err);
        setProducts([]);
        setFilteredProducts([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  /**
   * Handle search input changes
   * Filters products and resets to first page
   *
   * @param {Event} e - Input change event
   */
  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setPage(1);

    const filtered = filterProducts(products, query);
    setFilteredProducts(filtered);
  };

  // Flatten products for display
  const flattenedData = flattenProducts(filteredProducts);

  return {
    products,
    filteredProducts,
    flattenedData,
    searchQuery,
    page,
    rowsPerPage,
    loading,
    handleSearch,
    setPage,
  };
};
